import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import { formatDate, formatDateTime, formatMoney, formatNumber } from "@/features/shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  npwp: {
    fontSize: 9,
    marginTop: 2,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  summarySection: {
    marginTop: 16,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    minWidth: 200,
  },
});

export const PurchaseInvoicePdf = ({ purchaseInvoice, user }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Faktur Pembelian</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {purchaseInvoice.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Pembeli</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.buyer.name}
            </Text>
            {purchaseInvoice.buyer.taxInformation?.taxIdentificationNumber && (
              <Text style={styles.npwp}>
                NPWP: {purchaseInvoice.buyer.taxInformation.taxIdentificationNumber}
              </Text>
            )}
          </View>
          <View style={styles.infoItem}>
            <Text>Penjual</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.seller.name}
            </Text>
            {purchaseInvoice.seller.taxInformation?.taxIdentificationNumber && (
              <Text style={styles.npwp}>
                NPWP: {purchaseInvoice.seller.taxInformation.taxIdentificationNumber}
              </Text>
            )}
          </View>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(dayjs(purchaseInvoice.transactionDate))}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>No. Referensi Penjual</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.sellerReferenceNumber ?? "-"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Syarat Pembayaran</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.paymentTerm?.name ?? "-"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Rekening Bank</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.purchaseInvoiceSellerBankAccount
                ? `${purchaseInvoice.purchaseInvoiceSellerBankAccount.bank.name} - ${purchaseInvoice.purchaseInvoiceSellerBankAccount.accountNumber}`
                : "-"}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={[styles.infoItem, { flex: 2 }]}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {purchaseInvoice.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Products Table */}
        {purchaseInvoice.purchaseInvoiceProductVariants.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Detail Produk Varian</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.cell, { flex: 4 }]}>Nama</Text>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                Qty
              </Text>
              <Text style={[styles.cell, { flex: 1 }]}>Satuan</Text>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                Harga
              </Text>
              <Text style={[styles.cell, { flex: 1.5, textAlign: "right" }]}>
                Total
              </Text>
            </View>
          </>
        )}
        {purchaseInvoice.purchaseInvoiceProductVariants.map((variant) => {
          const originalTotal = variant.quantity * variant.price;
          const finalTotal = originalTotal - variant.discount;
          const hasDiscount = variant.discount && variant.discount > 0;

          return (
            <View style={styles.tableRow} key={variant.id}>
              <View style={[styles.cell, { flex: 4 }]}>
                <Text>{variant.name}</Text>
                <Text style={{ color: "#666", fontSize: 8, marginTop: 2 }}>
                  {variant.sku}
                </Text>
              </View>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                {variant.quantity}
              </Text>
              <Text style={[styles.cell, { flex: 1 }]}>
                {variant.product.unitOfMeasurement.name}
              </Text>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                {formatNumber(variant.price)}
              </Text>
              {!hasDiscount ? (
                <Text style={[styles.cell, { flex: 1.5, textAlign: "right", fontFamily: "Helvetica-Bold" }]}>
                  {formatNumber(finalTotal)}
                </Text>
              ) : (
                <View style={[styles.cell, { flex: 1.5, alignItems: "flex-end" }]}>
                  <Text style={{ fontFamily: "Helvetica-Bold" }}>
                    {formatNumber(finalTotal)}
                  </Text>
                  <Text
                    style={{
                      fontSize: 7,
                      color: "#666",
                      textDecoration: "line-through",
                      marginTop: 1,
                    }}
                  >
                    {formatNumber(originalTotal)}
                  </Text>
                </View>
              )}
            </View>
          );
        })}

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text>Subtotal:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(purchaseInvoice.subtotal)}
            </Text>
          </View>
          {purchaseInvoice.totalDiscount > 0 && (
            <View style={styles.summaryRow}>
              <Text>Total Diskon:</Text>
              <Text>{formatMoney(purchaseInvoice.totalDiscount)}</Text>
            </View>
          )}
          {purchaseInvoice.taxable && (
            <View style={styles.summaryRow}>
              <Text>Pajak ({purchaseInvoice.taxPercentage}%):</Text>
              <Text>{formatMoney(purchaseInvoice.taxAmount)}</Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>Grand Total:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(purchaseInvoice.grandTotal)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

PurchaseInvoicePdf.propTypes = {
  purchaseInvoice: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
